import { IconAccountCircle } from '@tvg/fuse-icons';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import styled from 'styled-components';
import { gridConfig } from '../../fuse-responsive-grid/src/grid-config';

const sizeConfig = {
	small: {
		fontSize: 'var(--font-size-m2)',
		// Multiplying by 1.6 due to root font-size of page being 10px and rounding to 1 decimal place. Original icon size is 24px
		iconHeight: `${Math.round((24 / 16) * 1.6 * 10) / 10}rem`,
		iconWidth: `${Math.round((24 / 16) * 1.6 * 10) / 10}rem`,
		lineHeight: 'var(--font-line-height-m2)',
	},
	medium: {
		fontSize: 'var(--font-size-m1)',
		// Multiplying by 1.6 due to root font-size of page being 10px and rounding to 1 decimal place. Original icon size is 32px
		iconHeight: `${Math.round((32 / 16) * 1.6 * 10) / 10}rem`,
		iconWidth: `${Math.round((32 / 16) * 1.6 * 10) / 10}rem`,
		lineHeight: 'var(--font-line-height-m1)',
	},
	large: {
		fontSize: 'var(--font-size-1)',
		// Multiplying by 1.6 due to root font-size of page being 10px and rounding to 1 decimal place. Original icon size is 40px
		iconHeight: `${Math.round((40 / 16) * 1.6 * 10) / 10}rem`,
		iconWidth: `${Math.round((40 / 16) * 1.6 * 10) / 10}rem`,
		lineHeight: 'var(--font-line-height-1)',
	},
};

const iconSizeCSS = (size) =>
	`
		width: ${sizeConfig[size].iconWidth};
		height: ${sizeConfig[size].iconHeight};
	`;

const fontSizeCSS = (size) =>
	`font: var(--font-weight-paragraph) ${sizeConfig[size].fontSize} / ${sizeConfig[size].lineHeight}  var(--font-brand)`;

const getViewportCSS = (size, callback) =>
	typeof size === 'string'
		? callback(size)
		: Object.entries(size)
				.map(
					([viewport, value]) =>
						` @media screen and (min-width: ${gridConfig[viewport].minWidth}) {
				${callback(value)}
			  }`,
				)
				.join('');

const StyledIconButton = styled.a.withConfig({ shouldForwardProp: (prop) => prop !== 'icon' })`
	${({ icon, size }) => {
		const SvgIcon = icon || <IconAccountCircle />;

		return `
        display: inline-flex;
        flex-direction: column;
        color: var(--color-global-grey900);
        align-items: center;
        text-decoration: none;
        padding: 0 var(--spacing-global-space1);
        position: relative;

        &:hover,
        &:focus {
            color: var(--color-brand-dark);
            cursor: pointer;
        }

        &:focus {
            outline: var(--spacing-global-space1) solid var(--color-functional-focus);
        }

        &:active {
            color: var(--color-global-black);
        }

        &:focus:before,
        &:hover:before {
            background: var(--color-brand-dark);
        }

        &:active:before {
            background: var(--color-global-black);
        }

        & span {
            border-bottom: 1px solid #0000;
            display: grid;
            ${getViewportCSS(size, fontSizeCSS)};
            min-height: 24px;
            place-items: center;
        }

        &:hover span {
            border-bottom: 1px solid var(--color-brand-dark);
        }

        &:active span {
            border-bottom: 1px solid var(--color-global-white);
        }

        & span.notification {
            border-radius: 8px;
            background-color: var(--color-brand-mid);
            color: var(--color-global-white);
            font: var(--font-weight-paragraph) var(--font-size-m2)/var(--font-line-height-m2) var(--font-brand);
            height: 16px;
            min-height: var(--spacing-global-space5);
            min-width: var(--spacing-global-space5);
            padding: 0 4px;
            position: absolute;
            top: 0;
            left: calc(50% + 4px);
        }

        &[disabled] {
            color: var(--color-global-grey400);
            background-color: unset;
            border: none;
            cursor: auto;
        }

        &[disabled] span.notification {
            background-color: var(--color-global-grey400);
        }

        &[disabled] span {
            border: none;
        }

        &[disabled]:hover span {
            border-bottom: none;
        }

        &[disabled]:before {
            background-color: var(--color-global-grey400);
        }

        &:before {
            content: '';
            display: inline-flex;
            margin: 0 0 var(--spacing-global-space1);
			${getViewportCSS(size, iconSizeCSS)}
            mask: url('data:image/svg+xml,${encodeURIComponent(
							renderToStaticMarkup(SvgIcon),
						)}') center/contain
                no-repeat;
            -webkit-mask: url('data:image/svg+xml,${encodeURIComponent(
							renderToStaticMarkup(SvgIcon),
						)}') center/contain
                no-repeat;
            background: var(--color-global-black);
        }

		// Minimum clickable area
		&:after {
			content: "";
			display: block;
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			width: 100%;
			height: 100%;
			min-width: 44px;
			min-height: 44px;
		}
    `;
	}}
`;

export { StyledIconButton, sizeConfig as iconButtonSizeConfig };
